import { investorApi } from '@core/api/httpClient'
import { CRYPTO_WALLET_ENDPOINTS } from './cryptoWalletEndpointPaths'
import {
  EMPTY_CRYPTO_WALLET,
  parseCryptoTransactions,
  parseCryptoWallet,
  type ParsedCryptoTransactionsPage,
} from './cryptoWalletParser'
import type { CryptoTransactionFilters, CryptoWallet } from '../types/cryptoWalletTypes'
import { normalizeError } from '@core/api/normalizeError'

const HTTP_NOT_FOUND = 404

// Both crypto endpoints document a 404 (see the OpenAPI spec) and return one
// for an investor who has never registered an XRPL address. That is an empty
// result, not a fault — mapping it here keeps the HTTP status at the API seam
// instead of leaking `statusCode === 404` checks into containers.
const isNotFound = (error: unknown): boolean =>
  normalizeError(error).statusCode === HTTP_NOT_FOUND

export async function getCryptoWallet(): Promise<CryptoWallet> {
  try {
    const { data } = await investorApi.get<unknown>(CRYPTO_WALLET_ENDPOINTS.WALLET)
    return parseCryptoWallet(data)
  } catch (error) {
    if (isNotFound(error)) return EMPTY_CRYPTO_WALLET
    throw error
  }
}

export interface FetchCryptoTransactionsParams {
  page: number
  limit: number
  filters: CryptoTransactionFilters
}

export async function getCryptoTransactions(
  params: FetchCryptoTransactionsParams,
): Promise<ParsedCryptoTransactionsPage> {
  const { filters } = params
  const query: Record<string, string | number> = {
    page: params.page,
    limit: params.limit,
    sort: filters.sort,
  }

  if (filters.search) query.search = filters.search
  // `status` accepts a comma-joined list; the chip row sets one at a time and
  // omits the param entirely for "All".
  if (filters.status) query.status = filters.status
  if (filters.dateFrom) query.dateFrom = filters.dateFrom
  if (filters.dateTo) query.dateTo = filters.dateTo
  if (filters.minQuantity) query.minTokenQuantity = filters.minQuantity
  if (filters.maxQuantity) query.maxTokenQuantity = filters.maxQuantity

  try {
    const { data } = await investorApi.get<unknown>(CRYPTO_WALLET_ENDPOINTS.TRANSACTIONS, {
      params: query,
    })
    return parseCryptoTransactions(data, params.limit)
  } catch (error) {
    // Same 404 contract as the wallet endpoint — no wallet means no activity.
    if (isNotFound(error)) return { rows: [], isLastPage: true }
    throw error
  }
}
